export interface VideoJoinTokenData {
  name?: string;
  roomId?: string;
  userType?: 'doctor' | 'patient';
  identity?: string;
}

/** Read Twilio JWT from video-new join URL (`t` or legacy `token`), or the token stored after a handoff redeem. */
const JOIN_TOKEN_STORAGE_KEY = 'VideoNewApp-joinToken';

export function storeJoinToken(token: string): void {
  const trimmed = (token || '').trim();
  if (!trimmed) {
    return;
  }
  try {
    sessionStorage.setItem(JOIN_TOKEN_STORAGE_KEY, trimmed);
  } catch {
    /* ignore quota / private mode */
  }
}

export function getJoinTokenFromUrl(): string {
  const urlParams = new URLSearchParams(window.location.search);
  const fromUrl = urlParams.get('t') || urlParams.get('token') || '';
  if (fromUrl) {
    storeJoinToken(fromUrl);
    return fromUrl;
  }
  try {
    return sessionStorage.getItem(JOIN_TOKEN_STORAGE_KEY) || '';
  } catch {
    return '';
  }
}

export function decodeJoinToken(token: string): VideoJoinTokenData | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }
    const payload = parts[1];
    const paddedPayload = payload + '='.repeat((4 - (payload.length % 4)) % 4);
    const decodedPayload = atob(paddedPayload);
    const tokenData = JSON.parse(decodedPayload);
    const identity = tokenData.grants?.identity || tokenData.identity || '';
    return {
      name: identity,
      roomId: tokenData.grants?.video?.room || tokenData.room,
      userType: detectUserTypeFromIdentity(identity),
      identity,
    };
  } catch {
    return null;
  }
}

export function detectUserTypeFromIdentity(identity: string): 'doctor' | 'patient' {
  if (!identity) {
    return 'patient';
  }
  const lowerIdentity = identity.toLowerCase();
  const doctorIndicators = ['dr.', 'dr ', 'doctor', 'doc', 'physician', 'md', 'dmd', 'dds', 'phd'];
  return doctorIndicators.some(indicator => lowerIdentity.includes(indicator)) ? 'doctor' : 'patient';
}

export function detectSessionUserType(identityHint?: string | null): 'doctor' | 'patient' {
  const token = getJoinTokenFromUrl();
  if (token) {
    const data = decodeJoinToken(token);
    if (data?.userType) {
      return data.userType;
    }
  }
  const fromHint = detectUserTypeFromIdentity(identityHint || '');
  if (fromHint === 'doctor') {
    return 'doctor';
  }
  const displayName = new URLSearchParams(window.location.search).get('n') || '';
  return detectUserTypeFromIdentity(displayName);
}
